import { isAtLeastRole, UserRole } from '@/enums';
import store from '@/store';
import { NavigationGuardNext, RouteLocationNormalized as Route } from 'vue-router';

import { AuthenticationStatus as Status} from '@/enums'

type beforeEnter = (to: Route, from: Route, next: NavigationGuardNext) => void

export const getHomePath = (role: UserRole) => {
    switch(role) {
        case UserRole.Admin:
            return '/'
        case UserRole.User:
            return '/'
        case UserRole.Moderator:
            return '/'
        default: 
            return '/forbidden'
    }
}

export const keepAliveHealthyTokens = () => {
    const expires = store.getters['identity/expires']
    if (!!store.getters['identity/jwToken'] && expires < Date.now()) {
        store.dispatch('identity/refresh')
    }
}

/**
 * Function builder
 * Builds a function to check for authorization for a specific role.
 * Also performs token validation
 * @param role The role a user should at least have.
 * @returns A function that checks authorization and token validation.
 */
const requiresRole = (role: UserRole): beforeEnter => {
    return (to: Route, from: Route, next: NavigationGuardNext) => {
        if (store.getters['identity/status'] === Status.LoggedIn && store.getters['identity/role'] === role) {
            next()
        } else {
            next({ name: "Login" })
        }
    }
}

/**
 * Function builder
 * Builds a function to check for authorization for a specific role.
 * Also performs token validation
 * @param role The role a user should at least have.
 * @returns A function that checks authorization and token validation.
 */
const requiresAtLeastRole = (role: UserRole): beforeEnter => {
    return (to: Route, from: Route, next: NavigationGuardNext) => {
        const currentRole: UserRole = store.getters['identity/role']
        if (store.getters['identity/status'] === Status.LoggedIn && isAtLeastRole(role)(currentRole)) {
            next()
        } else {
            next({ name: "Login" })
        }
    }
}

export const requiresEmployed = (to: Route, from: Route, next: NavigationGuardNext) => {
    const email = store.getters['identity/email']
    if (store.getters['identity/status'] === Status.LoggedIn 
        && ((email?.endsWith('@energievolutie.nl') ?? false) 
         || (email?.endsWith('@idea-nederland.nl') ?? false))) {
        next()
    } else {
        next({ name: "Login" })
    }
}

export const requiresAdmin = requiresRole(UserRole.Admin)
export const requiresModerator = requiresRole(UserRole.Moderator)
export const requiresUser = requiresRole(UserRole.User)

export const requiresAtLeastUser = requiresAtLeastRole(UserRole.User)
export const requiresAtLeastModerator = requiresAtLeastRole(UserRole.Moderator)


export const requiresModeratorOrUser = (to: Route, from: Route, next: NavigationGuardNext) => {
    if (store.getters['identity/status'] === Status.LoggedIn && 
        (store.getters['identity/role'] === UserRole.User || store.getters['identity/role'] === UserRole.Moderator)) {
        next()
    } else {
        next({ name: "Login" })
    }
}

export const logOut = (to: Route, from: Route, next: NavigationGuardNext) => {
    store.dispatch('identity/logout')
    next({ name: "Login" })
}